/**
 * Custom hook untuk mengecek ketersediaan kamar + rate plan.
 *
 * API Endpoint: GET `/api/v4/check-room-rate`
 * API Version: 4.0
 *
 * @param {string} checkin_date - Tanggal checkin
 * @param {string} checkout_date - Tanggal checkout
 * @returns {object} - Hasil CheckAvi yang telah diformat
 * @example
 * const { data, isLoading, error } = useCheckAvi("2022-12-31", "2023-01-01");
 */


import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { CheckAviResponse } from "../response/CheckAviResponse";
import { useAuth } from "../../../auth/context/AuthProvider";

interface CheckAviProps {
    checkin_date: string;
    checkout_date: string;
    booking_id?: string;
    room_maintenance_id?: string;
    property_id?: string; // Optional, default ke active property user
}

const useCheckAvi = ({ checkin_date, checkout_date, booking_id, room_maintenance_id, property_id }: CheckAviProps) => {
    const { user } = useAuth();
    // /v4/check-room-rate mewajibkan property_id (v3 mengambilnya dari active property di backend)
    const propertyId = property_id || user?.active_property_id;

    return useQuery({
        queryKey: ['booking', 'rooms', 'rates', checkin_date, checkout_date, propertyId, booking_id, room_maintenance_id],
        queryFn: async () => {
            try {
                const response = await privateApi.get(`/v4/check-room-rate`, {
                    params: {
                        checkin_date: checkin_date,
                        checkout_date: checkout_date,
                        property_id: propertyId,
                        ...(booking_id && { booking_id: booking_id }),
                        ...(room_maintenance_id && { room_maintenance_id: room_maintenance_id }),
                    }
                });

                // Validasi response
                const validationResult = CheckAviResponse.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid Availability data format");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch Availability`, error);
                throw error;
            }
        },
        enabled: !!checkin_date && !!checkout_date && !!propertyId,
        retry: import.meta.env.VITE_MAX_RETRY,
        refetchOnWindowFocus: false,
    });
};

export default useCheckAvi;
